import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { posApiFetch } from "@/utils/pos-api";

interface ReportExportDropdownProps {
  reportType: string;
  dateParams: string;
  disabled?: boolean;
}

const reportEndpoints: Record<string, { endpoint: string; label: string }> = {
  summary: { endpoint: "sales-summary", label: "Sales Summary" },
  analytics: { endpoint: "analytics", label: "Analytics" },
  breakdown: { endpoint: "analytics", label: "Sales Breakdown" },
  "top-items": { endpoint: "top-items", label: "Top Items" },
  payments: { endpoint: "payments", label: "Payments" },
  promotions: { endpoint: "discounts", label: "Promotions" },
  cancelled: { endpoint: "cancelled-orders", label: "Cancelled Orders" },
  locations: { endpoint: "locations", label: "Location Performance" },
};

export default function ReportExportDropdown({
  reportType,
  dateParams,
  disabled = false,
}: ReportExportDropdownProps) {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (format: "csv" | "pdf") => {
    const config = reportEndpoints[reportType];
    if (!config) {
      toast({
        title: "Export not available",
        description: "Export is not available for this report type",
        variant: "destructive",
      });
      return;
    }

    setIsExporting(true);
    try {
      const response = await posApiFetch(
        `/v1/pos/reports/export/${config.endpoint}?${dateParams}&format=${format}`
      );

      if (!response.ok) {
        throw new Error("Failed to export report");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `${config.label.toLowerCase().replace(/\s+/g, "-")}.${format === "pdf" ? "html" : "csv"}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Export successful",
        description: `${config.label} exported as ${format.toUpperCase()}`,
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: "Failed to export report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || isExporting}
          className="h-[36px]"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px]">
        <DropdownMenuLabel className="text-[12px] text-gray-500">
          Export Format
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => handleExport("csv")}
          className="cursor-pointer"
        >
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          <span>CSV File</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport("pdf")}
          className="cursor-pointer"
        >
          <FileText className="h-4 w-4 mr-2" />
          <span>PDF Report</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
